// ── TC3 — the Frameworks workspace contract ──────────────────────────────────────────────────

/**
 * ⚠ A DECOMPOSITION, NOT A SCORE — deliberately no `percent` field.
 *
 * A headline number that travels without its `excluded_na` and `unmapped` counts is the dishonest
 * artefact this model exists to avoid: on a sparsely-mapped framework the same work reads as 12%
 * or 80% depending entirely on whether unmapped requirements are counted as failures. The type
 * makes the omission structural — a consumer that wants a percentage has to take the counts, and
 * having taken them it has them to show.
 */
export interface FrameworkProgress {
  requirements_total: number;
  /** ⚠ No control maps here — a PLATFORM content gap, never a tenant failure. */
  unmapped: number;
  excluded_na: number;
  /** Mapped, but unassessed. Not 0%, not a failure. */
  not_started: number;
  implemented: number;
  /** Implemented, but reached only through `partial`/`supports` edges. A bucket, not a fraction. */
  partially_covered: number;
  partially_implemented: number;
  not_implemented: number;
  unknown: number;
  /**
   * ⚠ THE FOUR REQUIREMENT STATES, computed once in the API's classified CTE.
   * Requirements are MET; controls are IMPLEMENTED — and this surface must never say a
   * requirement is "implemented", which is what it said before these arrived.
   */
  met: number;
  partially_met: number;
  not_met: number;
  not_assessed: number;
}

/**
 * ⚠ Three states, the third DERIVED from history rather than stored.
 *   · `adopted`   — in the workspace now
 *   · `removed`   — previously used and stepped away from; restorable, work intact
 *   · `available` — entitled, never adopted
 * A `hidden` flag would have been a second representation of what the withdrawn adoption rows
 * already record.
 */
export type FrameworkStatus = 'adopted' | 'removed' | 'available';

export interface EntitledFramework {
  reference_id: string;
  display_code: string;
  name: string;
  description: string | null;
  version_label: string | null;
  publisher: string | null;
  adopted: boolean;
  adopted_at: string | null;
  status: FrameworkStatus;
  removed_at: string | null;
  progress: FrameworkProgress;
}

export interface RetainedCounts {
  controls: number;
  evidence: number;
  assessments: number;
  exceptions: number;
}

/** The honest denominator: what remains after removing what cannot fairly be scored. */
export function scorableTotal(p: FrameworkProgress): number {
  return p.requirements_total - p.excluded_na - p.unmapped;
}

/**
 * ⚠ Returns null rather than 0 when there is nothing to score.
 *
 * A framework with no requirements, or whose every requirement is unmapped or excluded, has NOT
 * scored zero — it has no measurement at all. Returning 0 would render "0%" over a framework
 * nobody has failed at, which is precisely the never-0% rule stated as a return type.
 */
export function scorePercent(p: FrameworkProgress): number | null {
  const total = scorableTotal(p);
  if (total <= 0) return null;
  // ⚠ `met`, not `implemented`. Numerically the same value today — but it is read from the
  //    REQUIREMENT axis the API computes, so this percentage cannot drift from the axis it
  //    claims to measure, and it never describes a requirement in control vocabulary.
  return Math.round((p.met / total) * 100);
}
